import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { PrimeReactProvider } from "@primereact/core/config";
import Aura from "@primeuix/themes/aura";
import {
  Autocomplete,
  Avatar,
  Badge,
  Button,
  Buttongroup,
  Card,
  Checkbox,
  Datatable,
  Dataview,
  Sidebar as SidebarStyles,
  Datepicker,
  Dialog,
  Fileupload,
  Fluid,
  Inputnumber,
  Inputtext,
  Message,
  Listbox,
  Menu,
  Metergroup,
  Overlaybadge,
  Popover,
  Progressspinner,
  Paginator,
  Select,
  Tabs,
  Tag,
  Splitter,
  Stepper,
  Textarea,
  Toast,
  Toggleswitch,
  Timeline,
  Toolbar,
} from "@primereact/styles";
import App from "./App.js";
import { AppConfigProvider } from "./lib/app-config.js";
import { DARK_THEME_CLASS } from "./lib/theme.js";
import i18n from "./i18n/index.js";
import "./index.css";
const auraNeutral = {
  ...Aura,
  semantic: {
    ...Aura.semantic,
    primary: {
      ...(Aura.semantic?.primary ?? {}),
      color: "#000000",
      contrastColor: "#ffffff",
      hoverColor: "#1f2937",
      activeColor: "#374151",
    },
  },
};

let queryClient: QueryClient;
queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => {
      // Every successful write can affect dashboard counts, list filters,
      // related entities and navigation destinations. Mark the full cache
      // stale so the next mounted screen always reflects server state.
      void queryClient.invalidateQueries();
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Keep <html lang> in sync with the active UI language.
i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});
document.documentElement.lang = i18n.language;

const stylesByComponentScope: Record<string, unknown> = {
  autocomplete: Autocomplete.styles,
  avatar: Avatar.styles,
  badge: Badge.styles,
  button: Button.styles,
  buttongroup: Buttongroup.styles,
  card: Card.styles,
  checkbox: Checkbox.styles,
  datatable: Datatable.styles,
  dataview: Dataview.styles,
  datepicker: Datepicker.styles,
  dialog: Dialog.styles,
  fileupload: Fileupload.styles,
  fluid: Fluid.styles,
  sidebar: SidebarStyles.styles,
  inputnumber: Inputnumber.styles,
  inputtext: Inputtext.styles,
  message: Message.styles,
  listbox: Listbox.styles,
  menu: Menu.styles,
  metergroup: Metergroup.styles,
  overlaybadge: Overlaybadge.styles,
  popover: Popover.styles,
  progressspinner: Progressspinner.styles,
  paginator: Paginator.styles,
  select: Select.styles,
  tabs: Tabs.styles,
  tag: Tag.styles,
  splitter: Splitter.styles,
  stepper: Stepper.styles,
  textarea: Textarea.styles,
  toast: Toast.styles,
  toggleswitch: Toggleswitch.styles,
  timeline: Timeline.styles,
  toolbar: Toolbar.styles,
};

const styledDefaults = new Proxy<
  Record<string, { props: { styles: unknown } }>
>(
  {},
  {
    get(_target, componentName) {
      const styles =
        stylesByComponentScope[
          String(componentName).split(".")[0]?.toLowerCase() ?? ""
        ];
      return styles ? { props: { styles } } : undefined;
    },
  },
);

const container = document.getElementById("root");
if (!container) throw new Error("Root element #root missing");

createRoot(container).render(
  <StrictMode>
    <PrimeReactProvider
      license={import.meta.env.VITE_PRIMEUI_LICENSE_KEY}
      ripple
      defaults={styledDefaults}
      theme={{
        preset: auraNeutral,
        options: { darkModeSelector: `.${DARK_THEME_CLASS}` },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AppConfigProvider>
          <App />
        </AppConfigProvider>
      </QueryClientProvider>
    </PrimeReactProvider>
  </StrictMode>,
);
